import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  useToast,
  VStack,
  SimpleGrid,
  Box,
  Text,
} from '@chakra-ui/react';
import { getData } from '../../utils/httpClient';
import { AdminField, useAdminFormFieldProps } from '../layout/AdminFormControls';

const CreateVehicleModal = ({
  isOpen,
  onClose,
  onCreate,
  initialCustomer = null,
}) => {
  const [formData, setFormData] = useState({
    id: null,
    plate: '',
    chassisNumber: '',
    engineNumber: '',
    kilometers: 0,
    model: '',
    brand: 'Volkswagen',
    year: 0,
    currentCustomerId: 0,
    colorId: 0,
    fuelTypeId: 0,
    representative: '',
    representativeContact: '',
  });

  const [brandsList, setBrandsList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [fuelTypeList, setFuelTypeList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const toast = useToast();
  const { fieldProps, selectProps } = useAdminFormFieldProps();

  // Get data from API
  const getBrands = async () => {
    try {
      const response = await getData('/brands');
      if (response?.payload?.brands) {
        const brands = [...response.payload.brands].sort((a, b) =>
          String(a?.name || '').localeCompare(String(b?.name || ''), 'es', {
            sensitivity: 'base',
          }),
        );
        setBrandsList(brands);
        const volkswagen = brands.find(
          (brand) => String(brand.name || '').trim().toLowerCase() === 'volkswagen',
        );
        if (volkswagen) {
          setFormData((prev) => {
            const current = String(prev.brand || '').trim().toLowerCase();
            if (current && current !== 'volkswagen') return prev;
            if (prev.brand === volkswagen.name) return prev;
            return { ...prev, brand: volkswagen.name };
          });
        }
      }
    } catch (error) {
      setBrandsList([]);
      toast({
        title: 'Error al obtener marcas',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getColors = async () => {
    try {
      const response = await getData('/colors');
      if (response?.payload?.colors) {
        setColorList(
          [...response.payload.colors].sort((a, b) =>
            String(a?.name || '').localeCompare(String(b?.name || ''), 'es', {
              sensitivity: 'base',
            }),
          ),
        );
      }
    } catch (error) {
      setColorList([]);
      toast({
        title: 'Error al obtener colores',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getFuelTypes = async () => {
    try {
      const response = await getData('/fuelTypes');
      if (response?.payload?.fuelTypes) {
        setFuelTypeList(response.payload.fuelTypes);
      }
    } catch (error) {
      setFuelTypeList([]);
      toast({
        title: 'Error al obtener tipos de combustible',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const term = customerSearch.trim();
    if (!isOpen || !term || term === selectedCustomer?.name) {
      setCustomersList([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '20', data: term });
        const response = await getData(`/customers?${params.toString()}`, {
          skipLoader: true,
        });
        if (!cancelled) setCustomersList(response?.payload?.customers || []);
      } catch (error) {
        if (!cancelled) {
          setCustomersList([]);
          toast({
            title: 'Error al obtener clientes',
            description: error.message || 'Ocurrió un error inesperado.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch, isOpen]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name || '');
    setCustomersList([]);
    setFormData((prev) => ({
      ...prev,
      currentCustomerId: customer.id,
      representative: '',
      representativeContact: '',
    }));
  };

  useEffect(() => {
    if (isOpen) {
      getBrands();
      getColors();
      getFuelTypes();
      if (initialCustomer?.id) {
        handleSelectCustomer(initialCustomer);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialCustomer?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Formatear valores numéricos
    const formattedValue =
      name === 'kilometers' || name === 'year' || name === 'currentCustomerId' || name === 'colorId' || name === 'fuelTypeId'
        ? value === ''
          ? '0'
          : String(Number(value))
        : value;

    // Si se cambia el cliente, limpiar el representante y su contacto
    const updatedFormData = { ...formData, [name]: formattedValue };
    if (name === 'currentCustomerId') {
      updatedFormData.representative = '';
      updatedFormData.representativeContact = '';
    }

    setFormData(updatedFormData);
  };

  const handleSave = () => {
    if (onCreate) {
      onCreate(formData);
    }
    onClose();
  };

  const handleClose = () => {
    // Limpiar formulario al cerrar
    setFormData({
      id: null,
      plate: '',
      chassisNumber: '',
      engineNumber: '',
      kilometers: 0,
      model: '',
      brand: 'Volkswagen',
      year: 0,
      currentCustomerId: 0,
      colorId: 0,
      fuelTypeId: 0,
    });
    setCustomerSearch('');
    setSelectedCustomer(null);
    setCustomersList([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
          Nuevo Vehículo
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3}>
              <AdminField label="Matrícula" isRequired>
                <Input name="plate" value={formData.plate} onChange={handleChange} placeholder="Matrícula" {...fieldProps} />
              </AdminField>
              <AdminField label="Chasis">
                <Input name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} placeholder="Nº chasis" {...fieldProps} />
              </AdminField>
              <AdminField label="Motor">
                <Input name="engineNumber" value={formData.engineNumber} onChange={handleChange} placeholder="Nº motor" {...fieldProps} />
              </AdminField>
              <AdminField label="Año">
                <Input name="year" type="number" value={formData.year} onChange={handleChange} placeholder="Año" {...fieldProps} />
              </AdminField>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3}>
              <AdminField label="Marca">
                <Select name="brand" value={formData.brand} onChange={handleChange} {...selectProps}>
                  <option value="" disabled>Seleccionar marca</option>
                  {brandsList?.map((brand) => (
                    <option key={brand.name} value={brand.name}>{brand.name}</option>
                  ))}
                </Select>
              </AdminField>
              <AdminField label="Modelo">
                <Input name="model" value={formData.model} onChange={handleChange} placeholder="Modelo" {...fieldProps} />
              </AdminField>
              <AdminField label="Color">
                <Select name="colorId" value={formData.colorId} onChange={handleChange} {...selectProps}>
                  <option value={0} disabled>Seleccionar color</option>
                  {colorList?.map((color) => (
                    <option key={color.id} value={color.id}>{color.name}</option>
                  ))}
                </Select>
              </AdminField>
              <AdminField label="Combustible">
                <Select name="fuelTypeId" value={formData.fuelTypeId} onChange={handleChange} {...selectProps}>
                  <option value={0} disabled>Seleccionar tipo</option>
                  {fuelTypeList?.map((fuelType) => (
                    <option key={fuelType.id} value={fuelType.id}>{fuelType.type}</option>
                  ))}
                </Select>
              </AdminField>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3}>
              <AdminField label="Kilómetros">
                <Input name="kilometers" type="number" value={formData.kilometers} onChange={handleChange} placeholder="Km" {...fieldProps} />
              </AdminField>
              <AdminField label="Cliente">
                <Box position="relative">
                  <Input
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setSelectedCustomer(null);
                      setFormData((prev) => ({ ...prev, currentCustomerId: 0 }));
                    }}
                    placeholder="Buscar por nombre o documento"
                    autoComplete="off"
                    {...fieldProps}
                  />
                  {customersList.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      zIndex={1500}
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="220px"
                      overflowY="auto"
                      mt={1}
                    >
                      {customersList.map((customer) => (
                        <Box
                          key={customer.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            {customer.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {customer.document || 'Sin documento'}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </AdminField>
              <AdminField label="Representante">
                <Input name="representative" value={formData.representative} onChange={handleChange} placeholder="Nombre" {...fieldProps} />
              </AdminField>
              <AdminField label="Contacto rep.">
                <Input name="representativeContact" value={formData.representativeContact} onChange={handleChange} placeholder="Tel. o email" {...fieldProps} />
              </AdminField>
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={handleClose}>
            Cancelar
          </Button>
          <Button size="sm" variant="adminPrimary" onClick={handleSave}>
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateVehicleModal;
